/**
 * AbrFilterSelector — dropdown to select an ABR filter on the dashboard.
 *
 * Shows "All Cases" (no filter) plus one entry per ABR filter.
 * Uses the Dropdown component from the design system.
 */

import { useAbrFilters } from '@moderation/hooks';
import { Dropdown, type DropdownOption } from '@soteria/design-system-ve';
import { useMemo } from 'react';

interface AbrFilterSelectorProps {
  communityId: string;
  selectedFilterId: string | undefined;
  onFilterChange: (filterId: string | undefined) => void;
}

const ALL_CASES_ID = '__all__';

export function AbrFilterSelector({ communityId, selectedFilterId, onFilterChange }: AbrFilterSelectorProps) {
  const { filters, loading } = useAbrFilters(communityId);

  const options: DropdownOption[] = useMemo(() => {
    const allCasesOption: DropdownOption = { id: ALL_CASES_ID, label: 'All Cases' };

    return [allCasesOption, ...filters.map(f => ({ id: f.id, label: f.name }))];
  }, [filters]);

  if (loading && filters.length === 0) {
    return null;
  }

  if (filters.length === 0) {
    return null;
  }

  return (
    <Dropdown
      aria-label="Queue filter"
      options={options}
      selectedItem={selectedFilterId ?? ALL_CASES_ID}
      size="medium"
      onSelectionChange={(value: string) => onFilterChange(value === ALL_CASES_ID ? undefined : value)}
    />
  );
}
